import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { PageState } from "@/components/common/PageState";
import { AdminAnalyticsDashboard } from "@/components/pages/analytics/admin/AdminAnalyticsDashboard";
import { FrontDeskAnalyticsDashboard } from "@/components/pages/analytics/front-desk/FrontDeskAnalyticsDashboard";
import { AnalyticsPageProvider } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Role } from "@/types/user";
import { getDefaultAuthorizedRoute } from "@/utils/defaultAuthorizedRoute";

const SUPER_ADMIN_ROLE = "SUPER_ADMIN";
const ADMIN_ANALYTICS_ROLES = new Set<string>([
  SUPER_ADMIN_ROLE,
  Role.TENANT_ADMIN,
]);
const FRONT_DESK_ANALYTICS_ROLES = new Set<string>([
  Role.HOTEL_ADMIN,
  Role.FRONT_DESK,
  Role.STAFF,
]);

type AnalyticsPageShellProps = {
  children: React.ReactNode;
};

const AnalyticsPageShell = ({
  children,
}: AnalyticsPageShellProps): React.JSX.Element => (
  <div className="h-full w-full overflow-auto bg-white p-6">{children}</div>
);

const isAdminAnalyticsRole = (role: string | undefined): boolean =>
  !!role && ADMIN_ANALYTICS_ROLES.has(role);

const isFrontDeskAnalyticsRole = (role: string | undefined): boolean =>
  !!role && FRONT_DESK_ANALYTICS_ROLES.has(role);

export const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();
  const { hotelId } = useParams<{ hotelId?: string }>();

  if (isLoading) {
    return (
      <AnalyticsPageShell>
        <PageState text={t("common.loading")} />
      </AnalyticsPageShell>
    );
  }

  if (isAdminAnalyticsRole(userProfile?.role)) {
    if (hotelId) {
      return (
        <AnalyticsPageShell>
          <AnalyticsPageProvider>
            <FrontDeskAnalyticsDashboard />
          </AnalyticsPageProvider>
        </AnalyticsPageShell>
      );
    }

    return (
      <AnalyticsPageShell>
        <AnalyticsPageProvider>
          <AdminAnalyticsDashboard />
        </AnalyticsPageProvider>
      </AnalyticsPageShell>
    );
  }

  if (isFrontDeskAnalyticsRole(userProfile?.role)) {
    return (
      <AnalyticsPageShell>
        <AnalyticsPageProvider>
          <FrontDeskAnalyticsDashboard />
        </AnalyticsPageProvider>
      </AnalyticsPageShell>
    );
  }

  return <Navigate replace to={getDefaultAuthorizedRoute(userProfile)} />;
};

export const FrontDeskAnalyticsRouteGuard: React.FC<
  AnalyticsPageShellProps
> = ({ children }) => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();

  if (isLoading) {
    return (
      <AnalyticsPageShell>
        <PageState text={t("common.loading")} />
      </AnalyticsPageShell>
    );
  }

  if (!isFrontDeskAnalyticsRole(userProfile?.role)) {
    return <Navigate replace to="/analytics" />;
  }

  return <>{children}</>;
};
